import React, { useEffect, useRef, useState } from "react";
import { X, Microphone, Stop, Play, Pause, ArrowCounterClockwise, PaperPlaneTilt, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { installVoiceEnhancer, setVoiceEnhancement } from "../lib/voiceEnhancer";
import { VOICE_PRESETS, REVERB_PRESETS, setVoicePreset, setReverbPreset, resetEffects, getEffectsState, isPresetAvailable } from "../lib/voiceEffects";
import VoiceClipPicker from "./VoiceClipPicker";

const MAX_SEC = 30;
const FX_REVERBS = ["none", "hall", "echo", "ktv", "concert"];

/**
 * VoiceRecorderSheet — record a fresh voice clip (max 30s) with live Voice FX,
 * preview it, then hand it back: onPick({ path, url, duration, title }).
 * Second tab reuses the library picker (AI clips / Voice Studio).
 */
export default function VoiceRecorderSheet({ open, onClose, onPick, title = "Voice comment" }) {
  const [tab, setTab] = useState("record");
  const [state, setState] = useState("idle"); // idle | recording | preview | uploading
  const [secs, setSecs] = useState(0);
  const [blob, setBlob] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [fx, setFx] = useState({ voice: "original", reverb: "none" });
  const [level, setLevel] = useState(0);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const meterRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    installVoiceEnhancer(); setVoiceEnhancement(true);
    const st = getEffectsState();
    setFx({ voice: st.voice, reverb: st.reverb });
    return () => { stopAll(); resetEffects(); setVoiceEnhancement(false); };
  }, [open]); // eslint-disable-line

  const stopAll = () => {
    clearInterval(timerRef.current); cancelAnimationFrame(meterRef.current);
    try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current?.__rawStream?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    audioRef.current?.pause();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const chunks = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 96000 });
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => { setBlob(new Blob(chunks, { type: "audio/webm" })); setState("preview"); };
      rec.start(250);
      recRef.current = rec;
      // Level meter
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC(); const src = ctx.createMediaStreamSource(stream); const an = ctx.createAnalyser(); an.fftSize = 256; src.connect(an);
      const buf = new Uint8Array(an.frequencyBinCount);
      const tick = () => { an.getByteFrequencyData(buf); setLevel(buf.reduce((a, b) => a + b, 0) / buf.length / 255); meterRef.current = requestAnimationFrame(tick); };
      tick();
      streamRef.current.__ctx = ctx;
      setSecs(0); setState("recording");
      timerRef.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_SEC) { stop(); return MAX_SEC; } return s + 1; }), 1000);
    } catch (e) { toast.error("Microphone access is needed to record"); }
  };

  const stop = () => {
    clearInterval(timerRef.current); cancelAnimationFrame(meterRef.current);
    try { recRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current?.__rawStream?.getTracks().forEach((t) => t.stop());
    streamRef.current?.__ctx?.close().catch(() => {});
    setLevel(0);
  };

  const togglePlay = () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    const a = new Audio(URL.createObjectURL(blob));
    a.onended = () => setPlaying(false);
    a.play().catch(() => {});
    audioRef.current = a; setPlaying(true);
  };

  const redo = () => { audioRef.current?.pause(); setPlaying(false); setBlob(null); setSecs(0); setState("idle"); };

  const send = async () => {
    setState("uploading");
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `comment-${Date.now()}.webm`, { type: "audio/webm" }));
      fd.append("title", `${title} · ${VOICE_PRESETS.find((p) => p.id === fx.voice)?.label || "Original"}`);
      fd.append("kind", "comment");
      fd.append("duration_sec", String(Math.max(1, secs)));
      const { data } = await api.post("/voice/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onPick({ path: data.storage_path, url: `/api/files/${data.storage_path}`, duration: data.duration_sec || secs, title: data.title });
      redo();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); setState("preview"); }
  };

  const pickVoice = (id) => { setVoicePreset(id); setFx((f) => ({ ...f, voice: id })); };
  const pickReverb = (id) => { setReverbPreset(id); setFx((f) => ({ ...f, reverb: id })); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[125] flex items-end" data-testid="voice-recorder-sheet">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full bg-ink rounded-t-3xl border-t border-white/10 p-5 max-h-[80vh] overflow-y-auto animate-fade-up">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-cream text-xl font-black tracking-tight flex items-center gap-2"><Microphone size={20} weight="fill" className="text-papaya" /> {title}</h3>
          <button onClick={onClose} data-testid="voice-recorder-close" className="p-2 rounded-full hover:bg-white/5"><X size={20} className="text-cream" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("record")} data-testid="voice-recorder-tab-record" className={`px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase border ${tab === "record" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>Record</button>
          <button onClick={() => setTab("library")} data-testid="voice-recorder-tab-library" className={`px-4 py-2 rounded-full text-[10px] font-black tracking-[0.2em] uppercase border flex items-center gap-1 ${tab === "library" ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}><FolderOpen size={12} weight="fill" /> From library</button>
        </div>

        {tab === "library" ? (
          <VoiceClipPicker open inline onClose={onClose} onPick={onPick} />
        ) : (
          <>
            {/* FX chips */}
            <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-1.5">Voice FX</p>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1" data-testid="voice-recorder-fx">
              {VOICE_PRESETS.filter((p) => isPresetAvailable(p.id)).map((p) => (
                <button key={p.id} onClick={() => pickVoice(p.id)} data-testid={`voice-fx-${p.id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${fx.voice === p.id ? "bg-sky-400 text-ink border-sky-400" : "bg-white/5 text-cream/70 border-white/10"}`}>{p.label}</button>
              ))}
            </div>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1 mt-1.5" data-testid="voice-recorder-reverb">
              {REVERB_PRESETS.filter((r) => FX_REVERBS.includes(r.id)).map((r) => (
                <button key={r.id} onClick={() => pickReverb(r.id)} data-testid={`voice-reverb-${r.id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${fx.reverb === r.id ? "bg-jam-gold text-ink border-jam-gold" : "bg-white/5 text-cream/70 border-white/10"}`}>{r.label}</button>
              ))}
            </div>

            {/* Recorder */}
            <div className="mt-5 flex flex-col items-center">
              <div className="relative">
                {state === "recording" && <span className="absolute inset-0 rounded-full bg-red-500/40 animate-rec-pulse" style={{ transform: `scale(${1 + level * 1.2})` }} />}
                {state === "idle" && (
                  <button onClick={start} data-testid="voice-recorder-start" className="relative w-20 h-20 rounded-full bg-papaya text-ink flex items-center justify-center papaya-glow"><Microphone size={32} weight="fill" /></button>
                )}
                {state === "recording" && (
                  <button onClick={stop} data-testid="voice-recorder-stop" className="relative w-20 h-20 rounded-full bg-red-500 text-cream flex items-center justify-center"><Stop size={30} weight="fill" /></button>
                )}
                {(state === "preview" || state === "uploading") && (
                  <button onClick={togglePlay} data-testid="voice-recorder-play" className="relative w-20 h-20 rounded-full bg-white/10 border border-white/20 text-cream flex items-center justify-center">{playing ? <Pause size={30} weight="fill" /> : <Play size={30} weight="fill" />}</button>
                )}
              </div>
              <p className="text-cream text-2xl font-black tabular-nums mt-3" data-testid="voice-recorder-timer">{String(Math.floor(secs / 60)).padStart(1, "0")}:{String(secs % 60).padStart(2, "0")} <span className="text-cream/40 text-sm">/ 0:{MAX_SEC}</span></p>
              <p className="text-cream/50 text-xs mt-1">{state === "idle" ? "Tap to record — FX apply live" : state === "recording" ? "Recording… tap to stop" : state === "uploading" ? "Posting…" : "Preview, then post"}</p>
            </div>

            {(state === "preview" || state === "uploading") && (
              <div className="flex gap-2 mt-5">
                <button onClick={redo} disabled={state === "uploading"} data-testid="voice-recorder-redo" className="flex-1 py-3 rounded-full bg-white/5 border border-white/15 text-cream text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><ArrowCounterClockwise size={14} weight="bold" /> Redo</button>
                <button onClick={send} disabled={state === "uploading"} data-testid="voice-recorder-send" className="flex-1 py-3 rounded-full bg-papaya text-ink text-[10px] font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5 disabled:opacity-40"><PaperPlaneTilt size={14} weight="fill" /> {state === "uploading" ? "Posting…" : "Post voice"}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
